import React, { useEffect, useRef } from 'react';
import { Animated, StyleProp, ViewStyle } from 'react-native';

export interface AnimatedEntranceProps {
  index?: number;
  delayStep?: number;
  maxDelay?: number;
  distance?: number;
  duration?: number;
  disabled?: boolean;
  style?: StyleProp<ViewStyle>;
  children: React.ReactNode;
}

/**
 * Fades + rises its children in on mount. Fast and subtle by default.
 * When `disabled` (reduced-motion or caller opt-out) it renders the final
 * state immediately with no animation. Reusable across redesign surfaces.
 */
export const AnimatedEntrance: React.FC<AnimatedEntranceProps> = ({
  index = 0,
  delayStep = 40,
  maxDelay = 160,
  distance = 8,
  duration = 200,
  disabled = false,
  style,
  children,
}) => {
  const progress = useRef(new Animated.Value(disabled ? 1 : 0)).current;

  useEffect(() => {
    if (disabled) return;
    const delay = Math.min(index * delayStep, maxDelay);
    const animation = Animated.timing(progress, {
      toValue: 1,
      duration,
      delay,
      useNativeDriver: true,
    });
    animation.start();
    return () => animation.stop();
    // Mount-only: the entrance plays once. Prop changes (e.g. index reordering
    // in a live list) must not restart it — the progress ref carries the value
    // across renders. disabled at mount => ref starts at 1, no animation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const translateY = progress.interpolate({
    inputRange: [0, 1],
    outputRange: [distance, 0],
  });

  return (
    <Animated.View style={[style, { opacity: progress, transform: [{ translateY }] }]}>
      {children}
    </Animated.View>
  );
};

AnimatedEntrance.displayName = 'AnimatedEntrance';
